import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Divider, Grid, IconButton, Paper, TextField, Typography } from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import ProfessionalPhotoField from '../components/pro/ProfessionalPhotoField';
import PhoneField from '../components/common/PhoneField';
import LszEntryPicker from '../components/conveyancer/LszEntryPicker';
import conveyancerService from '../services/conveyancerService';
import { brand, text, shadow } from '../theme/colors';

/** The conveyancer's own details, and which Law Society directory entry is theirs. */
const ConveyancerProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ displayName: '', phone: '', firmName: '', town: '', townsServed: '', bio: '', practitionerId: null });
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    conveyancerService.getMyProfile()
      .then((res) => {
        const p = res.data;
        setForm({
          displayName: p.display_name || '', phone: p.phone || '', firmName: p.firm_name || '', town: p.town || '',
          townsServed: (p.towns_served || []).join(', '), bio: p.bio || '', practitionerId: p.lsz_practitioner_id || null,
        });
        setPhoto(p.photo_display_url || null);
      })
      .catch((err) => { if (err.response?.status !== 404) setError('Failed to load your profile'); })
      .finally(() => setLoading(false));
  }, []);

  const search = useCallback((params) => conveyancerService.registerCandidates(params), []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await conveyancerService.saveMyProfile({
        displayName: form.displayName.trim(),
        phone: form.phone.trim() || null,
        firmName: form.firmName.trim() || null,
        town: form.town.trim() || null,
        townsServed: form.townsServed.split(',').map((c) => c.trim()).filter(Boolean),
        bio: form.bio || null,
        practitionerId: form.practitionerId,
      });
      const p = res.data;
      setForm((f) => ({ ...f, firmName: p.firm_name || f.firmName, town: p.town || f.town }));
      setSuccess('Profile saved');
    } catch (err) {
      const code = err.response?.data?.errorCode;
      if (['PRACTITIONER_NOT_FOUND', 'NOT_A_CONVEYANCER', 'PRACTITIONER_TAKEN'].includes(code)) setForm((f) => ({ ...f, practitionerId: null }));
      setError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: brand.primary }} /></Container>;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ color: text.secondary }} aria-label="Back to dashboard"><ArrowBack /></IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary }}>Conveyancer profile</Typography>
          <Typography variant="body2" sx={{ color: text.secondary }}>Your details, and your entry in the Law Society register</Typography>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      <Paper sx={{ p: 3, boxShadow: shadow.md, borderRadius: 2 }}>
        <Box sx={{ mb: 3 }}>
          <ProfessionalPhotoField previewUrl={photo} name={form.displayName}
            onUploaded={async (url) => {
              const r = await conveyancerService.saveMyProfile({ displayName: form.displayName.trim() || 'Conveyancer', photoUrl: url });
              setPhoto(r.data?.photo_display_url || null); setSuccess('Photo saved');
            }} />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Display name" value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneField label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <LszEntryPicker value={form.practitionerId} search={search}
              onChange={(id, p) => setForm((f) => ({ ...f, practitionerId: id, firmName: p?.firm || f.firmName, town: p?.town || f.town }))} />
            <Divider sx={{ mt: 2.5, mb: 1 }} />
          </Grid>
          <Grid item xs={12} md={7}>
            <TextField fullWidth label="Law firm" value={form.firmName} helperText="Filled from your Law Society entry; change it if you have moved"
              onChange={(e) => setForm({ ...form, firmName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField fullWidth label="Town" value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Towns you take transfers in" value={form.townsServed} helperText="Comma-separated, e.g. Harare, Gweru"
              onChange={(e) => setForm({ ...form, townsServed: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={3} label="About your practice" value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </Grid>
          <Grid item xs={12} sx={{ textAlign: 'right' }}>
            <Button variant="contained" startIcon={<Save />} disabled={saving || form.displayName.trim().length < 2} onClick={handleSave}
              sx={{ background: brand.gradient, fontWeight: 600, '&:hover': { background: brand.gradientHover } }}>
              {saving ? 'Saving…' : 'Save profile'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ConveyancerProfilePage;
